import autosize from "autosize";
import { ProgCss } from "./ProgCss.Generated";
import { wiredUpMarkerSymbol } from "./app";
import { openInitiallyOpenPopovers } from "./tooltips";
import { InitColumnTree } from "./ColumnTreeModule";
import { StartProgSelect } from "./ProgSelect";
import { WireUpMultiSelectionPopoverMenu } from "./GenericLijst";
import { WireUpColumnCheckBoxGroupToggle } from "./GenericLijst";
import { WireUpHoverText, WireupKolomHoverVoorLijsten } from "./LijstColumnHover";
import { Afletteren } from "./afletteren";
import { cast, castOrNull, ConditionalLog } from "./tools";
import * as tinyEditor from "./tinyMce_config";
import { BuildDevColorSwatch } from "./BuildDevColorSwatch";
import { delayedFocus, focusableChoicesWithin } from "./collectFuzzyFocusSelector";
import { extractRootOrganisatieFromPath } from "./portal/extractRootOrganisatieFromPath";
import { safeSessionStorage } from "./safeSessionStorage";

export function wireUpProgressJsControlsWithin(scopeEl: HTMLElement) {
    if (scopeEl[wiredUpMarkerSymbol] !== undefined) {
        //note that this sanity check can't catch all duplicate initializations because an element may have been initialized via it's parent.
        throw new Error(`This <${scopeEl.tagName}> has already been wired-up; this should happen at most once!`);
    }

    scopeEl[wiredUpMarkerSymbol] = true;
    whenSlideInOpenMakeSiblingsInert(scopeEl);
    registerDebugListener(scopeEl);
    InitLoginController(scopeEl);
    BuildDevColorSwatch(scopeEl);
    addClipboardTextAreaErrorStateHandler(scopeEl);
    wireUpTextInputUserShortcuts(scopeEl);
    WireUpHoverText(scopeEl);
    wireUpColorInputs(scopeEl);
    autoEnableAttachedButtonsOnInput(scopeEl);
    InitColumnTree(scopeEl);
    Afletteren(scopeEl);
    StartProgSelect(scopeEl);
    WireupKolomHoverVoorLijsten(scopeEl);
    WireUpMultiSelectionPopoverMenu(scopeEl);
    WireUpColumnCheckBoxGroupToggle(scopeEl);
    registerErrorTestButtonListener(scopeEl);
    registerFeedbackFormListeners(scopeEl);

    wireUpContentCopies(scopeEl);
    requestAnimationFrame(() => { autosize(scopeEl.querySelectorAll("textarea[data-autosize]")); });
    setTextAreaCounters(scopeEl);
    initAllTinyMCE(scopeEl);
    FocusGenericForm_IfNothingElseFocused(scopeEl);

    openInitiallyOpenPopovers(scopeEl); //Omdat popovers weer dingen aan de DOM toevoegt moet dit als laatste om duidelijk te houden dat die "dingen" maar een keer geinitialiseerd worden.

    autoScrollIfRequested(scopeEl);
    WireUpFileUploadClear(scopeEl);
}


function wireUpColorInputs(scopeEl: HTMLElement) {
    for (const colorPickerEl of scopeEl.getElementsByClassName(ProgCss.pnInputControl)) {
        if (colorPickerEl instanceof HTMLInputElement && colorPickerEl.type == 'color' && colorPickerEl.parentElement) {
            const textEl = document.createElement("span");
            const rerender = () => {
                textEl.textContent =  colorPickerEl.value;
                textEl.hidden = !colorPickerEl.value;
            };
            rerender();
            colorPickerEl.addEventListener('change', rerender);
            colorPickerEl.after(" ", textEl, " ");
        }
    }
}

function autoScrollIfRequested(scopeEl: HTMLElement) {
    const elementToScrollTo = [...scopeEl.getElementsByClassName(ProgCss.js_ScrollIntoViewElement)].slice(-1)[0];
    if (elementToScrollTo instanceof HTMLElement) {
        requestAnimationFrame(() => {
            ConditionalLog.log('scrolling to', elementToScrollTo);
            const rect = elementToScrollTo.getBoundingClientRect();
            const { clientHeight, clientWidth, scrollLeft, scrollTop } = document.documentElement;
            const yOffset = rect.height < clientHeight && scrollTop > 0 && rect.y + scrollTop + rect.height * 0.5 < clientHeight ? -scrollTop - 1
                : rect.y < 0 || rect.y > clientHeight * 0.5 && rect.y > clientHeight - rect.height ? rect.y - 10
                    : 0;
            const xOffset = rect.width < clientWidth && scrollLeft > 0 && rect.x + scrollLeft + rect.width * 0.5 < clientWidth ? -scrollLeft - 1
                : rect.x < 0 || rect.x > clientWidth * 0.5 && rect.x > clientWidth - rect.width ? rect.x - 10
                    : 0;
            if (xOffset || yOffset) {
                window.scrollBy(xOffset, yOffset);
                ConditionalLog.log('scrolling by', xOffset, yOffset, rect, clientHeight, clientWidth, scrollLeft, scrollTop);
            }
        });
    }
}

function whenSlideInOpenMakeSiblingsInert(scopeEl: HTMLElement) {
    for (const slideIn of scopeEl.getElementsByClassName(ProgCss.SlideInModule)) {
        if (slideIn.classList.contains(ProgCss.is_shown) && slideIn.parentElement) {
            for (const slideInSibling of slideIn.parentElement.children) {
                if (slideInSibling !== slideIn && slideInSibling instanceof HTMLElement) {
                    slideInSibling.inert = true;
                }
            }
        }
    }
    if (document.activeElement instanceof HTMLElement && document.activeElement.closest('[inert]')) {
        document.activeElement.blur();
    }
}

function registerDebugListener(scopeEl: HTMLElement) {
    const pnetModuleTooltipToggleCheckbox = castOrNull(scopeEl.querySelector("#pnetModuleTooltipToggle"), HTMLInputElement);
    if (pnetModuleTooltipToggleCheckbox) {
        pnetModuleTooltipToggleCheckbox.checked = document.documentElement.hasAttribute('data-debugshowids');
        pnetModuleTooltipToggleCheckbox.addEventListener("change", e => {
            if (pnetModuleTooltipToggleCheckbox.checked) {
                document.documentElement.setAttribute('data-debugshowids', '');
            } else {
                document.documentElement.removeAttribute('data-debugshowids');
            }
        });
    }


    const extraConsoleLogsCheckbox = castOrNull(scopeEl.querySelector("#extraConsoleLogs"), HTMLInputElement);
    ConditionalLog.enabled = ConditionalLog.enabled || safeSessionStorage?.getItem("ConditionalLog") == "y";
    if (extraConsoleLogsCheckbox) {
        extraConsoleLogsCheckbox.checked = ConditionalLog.enabled;
        extraConsoleLogsCheckbox.addEventListener("change", e => {
            console.log(extraConsoleLogsCheckbox.checked);
            ConditionalLog.enabled = extraConsoleLogsCheckbox.checked;
            try {
                if (ConditionalLog.enabled) {
                    safeSessionStorage?.setItem("ConditionalLog", "y");
                } else {
                    safeSessionStorage?.removeItem("ConditionalLog");
                }
            } catch { }
        });
    }
}

function registerFeedbackFormListeners(scopeEl: HTMLElement) {
    const feedbackForm = scopeEl.querySelector("#feedbackForm");
    if (feedbackForm instanceof HTMLFormElement) {
        feedbackForm.addEventListener("submit", async (e) => {
            e.preventDefault();
            await fetch(`/${extractRootOrganisatieFromPath()}/userfeedback`, {
                method: "POST",
                credentials: "same-origin",
                body: new FormData(e.currentTarget as HTMLFormElement)
            });
        });
    }
}

function initAllTinyMCE(scopeEl: HTMLElement) {
    for (const editor of scopeEl.getElementsByClassName(ProgCss.js_htmleditor)) {
        tinyEditor.render(cast(editor, HTMLElement));
    }
}

function upcaseFirst(inp: string) {
    const splitOn = RegExp(
        [
            "d'", "|o'", "|'d ", "|o' ", "|'s ", "| 's-", "|'s", "| op 't ", "| op ten ", "| op de ", "| ",
            "van het ", "| van der ", "| van de ", "| van den ", "| van ter ,", "| auf dem ", "| auf der ",
            "| von der ", "| von den ", "| vander ", "| in het ", "| in 't ", "| in de ", "| uit de ", "| ",
            " uit den ", "| uit het ", "| voor de ", "| voor 't ", "| aan het ", "| aan 't ", "| aan de ",
            "| aan den ", "| bij de ", "| de la ", "| del ", "| van ", "| von ", "| het ", "| de ", "| der ",
            "| den ", "| des ", "| di ", "| dos ", "| do ", "| du ", "| el ", "| le ", "| la ", "| lo ",
            "| los ", "| op ", "| te ", "| ten ", "| ter ", "| uit ", "| vd ", "| v.d. ", "| v\\/d , ",
            "| au ", "| aux ", "| à la ", "| a la ", "| a ", "| á  ",
            "| \\- ", "|\\s", "|\\s+", "|\\-+"
        ].join(""),
        "i");
    let outstr = inp;
    const strs = inp
        .replace(/\s+/g, " ")
        .replace(/\-+/g, "-")
        .replace(/('s)([a-zA-Z]+)/g, "$1 $2")
        .replace(/^\-+|\-+$/g, "")
        .split(splitOn);
    while (strs.length) {
        const word = strs.pop()!;
        outstr = outstr.replace(word, word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase());
    }
    return outstr;
};

function setTextAreaCounter(container: Element) {
    const area = cast(container.querySelector(`.${ProgCss.TextInputArea_container} textarea`), HTMLTextAreaElement);
    if (!area || !area.getAttribute("maxlength")) {
        return;
    }
    const telContainer = container.appendChild(document.createElement("div"));
    telContainer.classList.add(ProgCss.TextInputArea_tellertje);
    const countbox = telContainer.appendChild(document.createElement("span"));
    const mssgbox = telContainer.appendChild(document.createElement("span"));
    area.addEventListener("keyup", updateRemaining);
    updateRemaining();
    function updateRemaining() {
        const textboxValueLength = area.value.length;
        const maxAllowedlength = parseInt(area.getAttribute("maxlength")!, 10);
        const remainingCharacters = maxAllowedlength - textboxValueLength;
        countbox.textContent = String(textboxValueLength);
        countbox.classList.remove(ProgCss.u_error, ProgCss.charcount_ok);
        countbox.classList.add(remainingCharacters < 25 ? ProgCss.u_error : ProgCss.charcount_ok);
        const tekens_text = " teken" + (textboxValueLength === 1 ? "" : "s");
        const remaining_text = remainingCharacters <= 0
            ? ", maximaal aantal tekens bereikt."
            : remainingCharacters <= 10000 // onzin om hier een heeeel groot getal te laten zien.
                ? ", u kunt nog " + remainingCharacters + " teken(s) invoeren."
                : "";
        mssgbox.textContent = tekens_text + remaining_text;
        area.style.marginRight = '0.01px';
        requestAnimationFrame(() => requestAnimationFrame(() => area.style.marginRight = '0')); //FF baseline misalignment bug workaround
    }
}

function setTextAreaCounters(scopeEl: HTMLElement) {
    for (const container of scopeEl.getElementsByClassName(ProgCss.TextInputArea_container)) {
        setTextAreaCounter(container);
    };
};

function FocusGenericForm_IfNothingElseFocused(scopeEl: HTMLElement) {
    if (scopeEl === document.body && !delayedFocus.hasEventuallyActiveElement()) {
        const forms = new Set(scopeEl.getElementsByClassName(ProgCss.GenericForm));
        function isInForm(el: Node | null): boolean {
            while (true) {
                if (!(el instanceof Element)) {
                    return false;
                } else if (forms.has(el)) {
                    return true;
                }
                el = el.parentNode;
            }
        }

        const bestFocusable = focusableChoicesWithin(scopeEl)
            .map(el => ({ el, isInputControl: !(el.classList.contains(ProgCss.is_shownOnClick) || el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement), isInForm: isInForm(el) }))
            .filter(o => o.isInForm || o.isInputControl)
            .map(o => ({ el: o.el, cost: !o.isInputControl ? 2 : !o.isInForm ? 1 : 0 }))
            .sort((a, b) => a.cost - b.cost)[0];
        if (bestFocusable) {
            let shouldFocus = true;
            if (bestFocusable.cost > 0) {
                const { top, left, bottom, right } = bestFocusable.el.getBoundingClientRect();
                if (!(top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth)) {
                    shouldFocus = false;
                }
            }
            if (shouldFocus) {
                ConditionalLog.log("initial-input-autofocus", bestFocusable.el);
                bestFocusable.el.focus();
            } else {
                ConditionalLog.log("initial-input-autofocus SKPPED due to off-screen", bestFocusable.el, bestFocusable.cost);
            }
        }
    }
}


function InitLoginController(scopeEl: HTMLElement) {
    const saveLoginMethodKey = "LoginController_defaultMethod";
    function getStoredAuthMethod() {
        try {
            return window.localStorage.getItem(saveLoginMethodKey);
        } catch {
            return null;
            //localStorage is not always reliable
        }
    }

    function setStoredAuthMethod(authMethod: string | null) {
        try {
            if (authMethod !== null) {
                window.localStorage.setItem(saveLoginMethodKey, authMethod);
            } else {
                window.localStorage.removeItem(saveLoginMethodKey);
            }
        } catch {
            //ignore private mode related quota errors
        }
    }

    const devCheckbox = castOrNull(scopeEl.querySelector('#pnetPreselectAuthGoogle'), HTMLInputElement);
    if (devCheckbox) {
        devCheckbox.checked = getStoredAuthMethod() === 'Google';
        devCheckbox.addEventListener("change", () => setStoredAuthMethod(devCheckbox.checked ? 'Google' : null));
    }

    const methodeSelect = castOrNull(scopeEl.querySelector('#LoginController_MethodeSelect'), HTMLSelectElement);
    if (methodeSelect) {
        const initialMethod = methodeSelect.value;
        const loginNaamRow = scopeEl.querySelector('#LoginController_LoginNaam')!.closest("tr")!;
        const passwordInputRow = scopeEl.querySelector('#LoginController_Password')!.closest("tr")!;
        const saveMethodCheckBox = castOrNull(scopeEl.querySelector('#LoginController_SaveMethod'), HTMLInputElement);
        const saveMethodRow = saveMethodCheckBox?.closest("tr");

        const savedMethod = getStoredAuthMethod();
        if (savedMethod && savedMethod !== initialMethod && saveMethodCheckBox) {
            methodeSelect.value = savedMethod;
            if (!methodeSelect.value) {
                methodeSelect.value = initialMethod;
            }
            saveMethodCheckBox.checked = methodeSelect.value !== initialMethod;
        }


        const syncLoginMethod = function () {
            const isPasswordLogin = methodeSelect.value === 'ProgressNet';
            loginNaamRow.hidden = !isPasswordLogin;
            passwordInputRow.hidden = !isPasswordLogin;
            if (saveMethodRow) {
                saveMethodRow.hidden = methodeSelect.value === initialMethod
                    || document.documentElement.getAttribute("data-niet-productie-omgeving") == null && saveMethodCheckBox?.checked !== true;
            }
        };

        methodeSelect.form?.addEventListener("submit", e => setStoredAuthMethod(saveMethodRow && !saveMethodRow.hidden && saveMethodCheckBox?.checked ? methodeSelect.value : null));

        syncLoginMethod();
        methodeSelect.addEventListener('change', syncLoginMethod);
    }

    const input = scopeEl.querySelector('#LoginController_HiddenScreenResolution') as HTMLInputElement | null;
    if (input) {
        input.value = JSON.stringify({
            screenWidth: screen.width,
            screenHeight: screen.height,
            availWidth: screen.availWidth,
            availHeight: screen.availHeight,
            colorDepth: screen.colorDepth,
            innerWidth,
            innerHeight,
            outerHeight,
            outerWidth
        });
    }


}

function wireUpTextInputUserShortcuts(scopeEl: HTMLElement) {
    for (const textInputEl of scopeEl.querySelectorAll("input")) {
        if (textInputEl.classList.contains(ProgCss.js_isUppercasableField)) {
            textInputEl.addEventListener(
                "keydown",
                e => {
                    if (!(e.shiftKey && e.which === 38)) {
                        return;
                    }
                    e.preventDefault();
                    textInputEl.value = upcaseFirst(textInputEl.value);
                    textInputEl.focus();
                });
        }

        const attachedToButtonId = textInputEl.getAttribute("data-AttachedToButtonId");
        const targetButton = attachedToButtonId && document.getElementById(attachedToButtonId);
        if (targetButton instanceof HTMLElement) {
            textInputEl.addEventListener(
                "keypress",
                e => {
                    const enterKeycode = 13;
                    if (cast(e, KeyboardEvent).keyCode === enterKeycode) {
                        e.preventDefault();
                        targetButton.click();
                    }
                });
        }
    }
}

function autoEnableAttachedButtonsOnInput(scopeEl: HTMLElement) {
    for (const textInputEl of scopeEl.querySelectorAll("*[data-EnableAttachedButtonOnChange='true'][data-AttachedToButtonId]")) {
        const attachedToButtonId = textInputEl.getAttribute("data-AttachedToButtonId")!;
        const targetButton = cast(document.getElementById(attachedToButtonId!), HTMLButtonElement);
        textInputEl.addEventListener("input", () => targetButton.disabled = false);
    }
}


function addClipboardTextAreaErrorStateHandler(scopeEl: HTMLElement) {
    const clipboardTextArea = castOrNull(scopeEl.querySelector(`.${ProgCss.js_clipboard_textarea}`), HTMLTextAreaElement);
    const clipboardUpdateButton = castOrNull(scopeEl.querySelector(`.${ProgCss.clipboard_update_button}`), HTMLButtonElement);

    if (!clipboardTextArea || !clipboardUpdateButton) {
        return;
    }

    clipboardTextArea.addEventListener(
        "input",
        () => {
            if (clipboardUpdateButton.classList.contains(ProgCss.ErrorState)) {
                const clipboardIgnoreButton = castOrNull(scopeEl.querySelector(`.${ProgCss.js_clipboard_ignore_button}`), HTMLButtonElement);

                if (!clipboardIgnoreButton) {
                    throw new Error("Could not find ignore-update button clipboard!");
                }

                clipboardUpdateButton.style.display = "inline-block";
                clipboardIgnoreButton.style.display = "none";
            }
        });
}

function registerErrorTestButtonListener(scopeEl: HTMLElement) {
    for (const htmlButton of scopeEl.querySelectorAll("button")) {
        const errorMessage = htmlButton.getAttribute("data-onclick-error-test");
        if (errorMessage) {
            htmlButton.addEventListener("click", () => {
                throw new Error(errorMessage);
            });
        }
    }
}

function wireUpContentCopies(scopeEl: HTMLElement) {
    for (const contentCopy of scopeEl.getElementsByClassName(ProgCss.ContentCopy)) {
        contentCopy.addEventListener("click", () => {
            const content = contentCopy.getElementsByClassName(ProgCss.ContentCopy_text)[0]?.textContent;
            if (content != null) {
                navigator.clipboard.writeText(content);
                contentCopy.classList.add(ProgCss.is_copied);
            } else {
                throw new Error("Unexpected: no text-content to copy to clipboard");
            }
        });

        contentCopy.addEventListener("mouseleave", () => {
            setTimeout(() => {
                contentCopy.classList.remove(ProgCss.is_copied);
            }, 1000);
        })
    }
}

export function WireUpFileUploadClear(scopeEl: HTMLElement) {
    for (const el of scopeEl.querySelectorAll("[data-clear-fileid]")) {
        const inputEl = cast(el, HTMLButtonElement);
        inputEl.addEventListener("click",
            (e) => {
                e.preventDefault();
                const hiddenInput = castOrNull(scopeEl.querySelector(`input[name=${el.getAttribute("data-clear-fileid")}]`), HTMLInputElement);
                if (hiddenInput != null) {
                    hiddenInput.value = "";
                    hiddenInput.form?.requestSubmit();
                }
            }
        );
    }
}
